'use client'

// Interlude · "In the Field"
// Full-width 3D curved photo slider (coverflow-style).
// Photos laid out along an invisible arc: center head-on, sides rotated
// toward the viewer. Auto-advances every 4s, drag/throw with inertia,
// click-to-center, arrow buttons, dot jump-nav, caption crossfade.

import { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { Draggable } from 'gsap/Draggable'
import { InertiaPlugin } from 'gsap/InertiaPlugin'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsapSetup } from '@/lib/animations/gsapSetup'
import { useReducedMotion } from '@/lib/animations/useReducedMotion'

// Keep InertiaPlugin as a runtime import so tree-shaking doesn't drop it.
void InertiaPlugin

// ─── Photos ─────────────────────────────────────────────────────────────────
// Captions + alts default to messages/*.json under interlude.photos.pN{Caption,Alt}
// — also overridable per item if the CMS provides them directly.
export type InterludePhoto = {
  src: string
  /** Stable key like "p1"…"p8" used for fallback caption/alt lookup via tp(`${key}Caption`) */
  key: string
  /** Direct caption + alt (preferred when provided; falls back to translations) */
  caption?: string
  alt?: string
}

const PHOTOS_FALLBACK: InterludePhoto[] = [
  { src: '/Stock%20Equipo/BORT3036.jpg',                key: 'p1' },
  { src: '/Stock%20Equipo/BORT3052.jpg',                key: 'p2' },
  { src: '/Stock%20Equipo/BORT3058.jpg',                key: 'p3' },
  { src: '/Stock%20Equipo/BORT3071.jpg',                key: 'p4' },
  { src: '/Stock%20Equipo/BORT3085.jpg',                key: 'p5' },
  { src: '/Stock%20Equipo/BORT3095.jpg',                key: 'p6' },
  { src: '/HOME/Premios/54579287501_1c5e5c6db5_o.jpg',  key: 'p7' },
  { src: '/Stock%20Equipo/IMG-20251021-WA0056.jpg',     key: 'p8' },
]

// ─── Tokens ──────────────────────────────────────────────────────────────────
const CREAM      = '#F3EAC8'
const CREAM_90   = 'rgba(243,234,200,0.90)'
const CREAM_60   = 'rgba(243,234,200,0.60)'
const CREAM_30   = 'rgba(243,234,200,0.30)'
const EARTH_INK  = '#3D2E0E'
const EARTH_DARK = 'rgba(20,14,4,0.94)'
const EARTH_MID  = 'rgba(61,46,14,0.72)'
const INK_LOW    = 'rgba(61,46,14,0.34)'
const OLIVE      = '#5B7005'
const LIME       = '#9FAF08'
const FONT_BODY  = 'var(--font-body)'
const FONT_DISP  = 'var(--font-display)'
const FONT_SERIF = 'var(--font-serif)'
const IMG_FILTER = 'sepia(8%) saturate(92%) brightness(1.02) contrast(1.04)'

const pad = (n: number) => String(n).padStart(2, '0')

// ─── Coverflow math ─────────────────────────────────────────────────────────
// Signed distance (supports fractional) that wraps cyclically in [-N/2, N/2).
function dist(photoIdx: number, centerIdx: number, total: number): number {
  let n = photoIdx - centerIdx
  const half = total / 2
  if (n >  half) n -= total
  if (n <= -half) n += total
  return n
}

interface CurveConfig {
  cardW:  number
  cardH:  number
  overlap: number
  rotY:   number
  zStep:  number
}

const DESKTOP: CurveConfig = { cardW: 360, cardH: 480, overlap: 70, rotY: 25, zStep: 80 }
const MOBILE:  CurveConfig = { cardW: 240, cardH: 320, overlap: 50, rotY: 20, zStep: 60 }

interface PhotoTransform {
  x: number; rotationY: number; z: number
  scale: number; opacity: number; zIndex: number
}

function transformFor(n: number, cfg: CurveConfig): PhotoTransform {
  const abs = Math.abs(n)
  return {
    x:         n * (cfg.cardW - cfg.overlap),
    rotationY: n * -cfg.rotY,
    z:         -abs * cfg.zStep,
    scale:     Math.max(0.5, 1 - abs * 0.08),
    opacity:   Math.max(0,   1 - abs * 0.15),
    zIndex:    Math.round(100 - abs),
  }
}

// ─── Lightbox ───────────────────────────────────────────────────────────────
interface LightboxProps {
  photos:    InterludePhoto[]
  open:      boolean
  photoIdx:  number
  onClose:   () => void
  onPrev:    () => void
  onNext:    () => void
  onJump:    (idx: number) => void
}

function Lightbox({ photos: PHOTOS, open, photoIdx, onClose, onPrev, onNext, onJump }: LightboxProps) {
  const N = PHOTOS.length
  const overlayRef  = useRef<HTMLDivElement>(null)
  const stageRef    = useRef<HTMLDivElement>(null)
  const closeBtnRef = useRef<HTMLButtonElement>(null)
  const firstChangeRef = useRef(true)
  const closingRef  = useRef(false)

  const reduced = useReducedMotion()
  const t  = useTranslations('interlude.lightbox')
  const tp = useTranslations('interlude.photos')

  // Open animation + body scroll lock
  useLayoutEffect(() => {
    if (!open) return
    document.body.style.overflow = 'hidden'
    closingRef.current = false
    firstChangeRef.current = true
    return () => { document.body.style.overflow = '' }
  }, [open])

  useEffect(() => {
    if (!open) return
    const ov = overlayRef.current
    const st = stageRef.current
    if (!ov || !st) return
    if (reduced) {
      gsap.set(ov, { autoAlpha: 1 })
      gsap.set(st, { autoAlpha: 1, scale: 1 })
      return
    }
    gsap.timeline()
      .set(ov, { autoAlpha: 0 })
      .set(st, { autoAlpha: 0, scale: 0.95 })
      .to(ov,  { autoAlpha: 1, duration: 0.28, ease: 'power2.out' })
      .to(st,  { autoAlpha: 1, scale: 1, duration: 0.45, ease: 'power3.out' }, '-=0.14')
  }, [open, reduced])

  // Focus close button on open
  useEffect(() => {
    if (open && closeBtnRef.current) closeBtnRef.current.focus()
  }, [open])

  // Animated close — runs GSAP timeline, then unmounts via onClose
  const handleClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    const ov = overlayRef.current
    const st = stageRef.current
    if (!ov || !st || reduced) { onClose(); return }
    gsap.timeline({ onComplete: onClose })
      .to(st, { autoAlpha: 0, scale: 0.96, duration: 0.22, ease: 'power2.in' })
      .to(ov, { autoAlpha: 0, duration: 0.2, ease: 'power2.in' }, '-=0.12')
  }, [onClose, reduced])

  // Photo crossfade on prev/next (skip first render)
  useEffect(() => {
    if (!open) return
    if (firstChangeRef.current) { firstChangeRef.current = false; return }
    const img = stageRef.current?.querySelector('img')
    if (!img || reduced) return
    gsap.fromTo(img,
      { autoAlpha: 0.4, scale: 0.98 },
      { autoAlpha: 1, scale: 1, duration: 0.35, ease: 'power2.out' },
    )
  }, [photoIdx, open, reduced])

  // Keyboard — Esc closes, arrows navigate
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if      (e.key === 'Escape')     { e.preventDefault(); handleClose() }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); onPrev() }
      else if (e.key === 'ArrowRight') { e.preventDefault(); onNext() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, handleClose, onPrev, onNext])

  if (!open) return null

  const photo = PHOTOS[photoIdx]
  const photoCaption = photo.caption ?? tp(`${photo.key}Caption`)
  const photoAlt     = photo.alt     ?? tp(`${photo.key}Alt`)

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('label', { n: photoIdx + 1, total: N, alt: photoAlt })}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose() }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: EARTH_DARK,
        backdropFilter: 'blur(24px) saturate(140%)',
        WebkitBackdropFilter: 'blur(24px) saturate(140%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(72px, 8vh, 104px) clamp(72px, 8vw, 120px)',
      }}
    >
      {/* Grain texture */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/grain.svg)',
        backgroundSize: '200px 200px',
        opacity: 0.09, mixBlendMode: 'screen',
        pointerEvents: 'none',
      }} />

      {/* Corner crops (editorial) */}
      {(['tl','tr','bl','br'] as const).map(corner => {
        const d = { tl:'M0 10 L0 0 L10 0', tr:'M8 0 L18 0 L18 10', bl:'M0 8 L0 18 L10 18', br:'M8 18 L18 18 L18 8' }[corner]
        const pos = { tl:{top:20,left:20}, tr:{top:20,right:20}, bl:{bottom:20,left:20}, br:{bottom:20,right:20} }[corner]
        return (
          <svg key={corner} aria-hidden="true" viewBox="0 0 18 18"
            style={{ position:'absolute', width:18, height:18, ...pos, pointerEvents:'none', zIndex:11 }}>
            <path d={d} fill="none" stroke={CREAM_30} strokeWidth={1} />
          </svg>
        )
      })}

      {/* Top chrome */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 72,
        padding: '20px clamp(24px, 4vw, 56px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 12,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 20,
          fontFamily: FONT_BODY, fontSize: 10.5,
          letterSpacing: '0.38em', textTransform: 'uppercase',
          fontWeight: 600, color: CREAM_60,
        }}>
          <span>{t('chromeLeft')}</span>
          <span aria-hidden="true" style={{ display:'inline-block', width:1, height:12, background:CREAM_30 }} />
          <span style={{ color: LIME }}>
            <span style={{ fontVariantNumeric:'tabular-nums' }}>{pad(photoIdx+1)}</span>
            <span style={{ opacity:0.5, margin:'0 6px' }}>/</span>
            <span style={{ fontVariantNumeric:'tabular-nums' }}>{pad(N)}</span>
          </span>
        </div>
        <button
          ref={closeBtnRef}
          type="button"
          onClick={handleClose}
          aria-label={t('close')}
          className="ilb-close"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="5" y1="5" x2="19" y2="19" />
            <line x1="19" y1="5" x2="5" y2="19" />
          </svg>
        </button>
      </div>

      {/* Image + caption (flow-centered by parent flex) */}
      <div
        ref={stageRef}
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(18px, 2.5vh, 28px)',
          maxWidth: 'min(92vw, 1100px)',
          zIndex: 5,
          willChange: 'transform, opacity',
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={photo.src}
          alt={photoAlt}
          loading="lazy"
          decoding="async"
          style={{
            display: 'block',
            maxWidth: '100%',
            maxHeight: 'calc(100vh - 260px)',
            borderRadius: 8,
            boxShadow: '0 32px 96px rgba(0,0,0,0.5), 0 8px 20px rgba(0,0,0,0.3)',
            filter: IMG_FILTER,
            objectFit: 'contain',
          }}
        />
        <p style={{
          fontFamily: FONT_SERIF, fontStyle: 'italic',
          fontSize: 'clamp(1rem, 1.3vw, 1.2rem)',
          color: CREAM_90,
          textAlign: 'center',
          margin: 0,
          maxWidth: '60ch',
          lineHeight: 1.4,
        }}>
          {photoCaption}
        </p>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={onPrev}
        aria-label={t('prev')}
        className="ilb-arrow ilb-arrow-left"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={onNext}
        aria-label={t('next')}
        className="ilb-arrow ilb-arrow-right"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>

      {/* Dot indicators */}
      <div
        role="tablist"
        aria-label={t('tablist')}
        style={{
          position: 'absolute',
          bottom: 32, left: 0, right: 0,
          display: 'flex', justifyContent: 'center', gap: 8,
          zIndex: 11,
        }}
      >
        {PHOTOS.map((_, i) => {
          const active = i === photoIdx
          return (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={t('jumpLabel', { n: i + 1 })}
              className={`ilb-dot ${active ? 'active' : ''}`}
              onClick={() => onJump(i)}
            />
          )
        })}
      </div>
    </div>
  )
}

// ─── Component ──────────────────────────────────────────────────────────────
export default function InterludeClient({ photos }: { photos?: InterludePhoto[] } = {}) {
  // Use the CMS-provided list when available, else fall back to bundled paths.
  const PHOTOS: InterludePhoto[] = photos && photos.length > 0 ? photos : PHOTOS_FALLBACK
  const N = PHOTOS.length

  const sectionRef = useRef<HTMLElement>(null)
  const introRef   = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const stageRef   = useRef<HTMLDivElement>(null)
  const dragProxyRef = useRef<HTMLDivElement>(null)
  const captionRef = useRef<HTMLParagraphElement>(null)
  const dotsRef    = useRef<HTMLDivElement>(null)
  const photoRefs  = useRef<(HTMLDivElement | null)[]>([])
  photoRefs.current = photoRefs.current.slice(0, N)

  const t   = useTranslations('interlude')
  const tp  = useTranslations('interlude.photos')

  // Helper: prefer the CMS-provided caption when present, fall back to the
  // bundled translation key (`${key}Caption`). Same for alt text.
  const captionFor = (i: number) => PHOTOS[i].caption ?? tp(`${PHOTOS[i].key}Caption`)

  const [currentIdx,    setCurrentIdx]    = useState(0)
  const [displayedCap,  setDisplayedCap]  = useState<string>(captionFor(0))
  const [isMobile,      setIsMobile]      = useState(false)
  const [hoverChrome,   setHoverChrome]   = useState(false)
  const [lightboxOpen,  setLightboxOpen]  = useState(false)

  const reduced = useReducedMotion()
  const currentIdxRef = useRef(currentIdx)
  useEffect(() => { currentIdxRef.current = currentIdx }, [currentIdx])

  const cfg = isMobile ? MOBILE : DESKTOP
  const cfgRef = useRef(cfg)
  useEffect(() => { cfgRef.current = cfg }, [cfg])

  // Autoplay control — pauses on hover/drag with graceful resume
  const [autoPaused, setAutoPaused] = useState(false)
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pauseAuto = useCallback(() => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    setAutoPaused(true)
  }, [])
  const scheduleResumeAuto = useCallback((delayMs: number) => {
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current)
    resumeTimerRef.current = setTimeout(() => setAutoPaused(false), delayMs)
  }, [])

  // ── Mobile detection ─────────────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handle = (m: MediaQueryList | MediaQueryListEvent) => setIsMobile(m.matches)
    handle(mq)
    mq.addEventListener('change', handle)
    return () => mq.removeEventListener('change', handle)
  }, [])

  // ── Transform application ────────────────────────────────────────────────
  // Accepts fractional index so drag updates produce smooth motion.
  const applyTransforms = useCallback((idx: number, instant = false, duration = 0.9) => {
    const c = cfgRef.current
    photoRefs.current.forEach((el, i) => {
      if (!el) return
      const n = dist(i, idx, N)
      const t = transformFor(n, c)
      if (instant) {
        gsap.set(el, {
          x: t.x, rotationY: t.rotationY, z: t.z,
          scale: t.scale, autoAlpha: t.opacity, zIndex: t.zIndex,
        })
      } else {
        gsap.to(el, {
          x: t.x, rotationY: t.rotationY, z: t.z,
          scale: t.scale, autoAlpha: t.opacity, zIndex: t.zIndex,
          duration, ease: 'power3.out', overwrite: 'auto',
        })
      }
    })
  }, [])

  // Tween on index change (no entrance animation — that's separate)
  useEffect(() => {
    applyTransforms(currentIdx, false, 0.9)
  }, [currentIdx, applyTransforms])

  // Re-apply on mobile/desktop config change
  useEffect(() => {
    applyTransforms(currentIdxRef.current, true)
  }, [isMobile, applyTransforms])

  // ── Caption crossfade ────────────────────────────────────────────────────
  useEffect(() => {
    const el = captionRef.current
    if (!el) return
    if (reduced) {
      setDisplayedCap(captionFor(currentIdx))
      return
    }
    gsap.timeline()
      .to(el, { opacity: 0, duration: 0.2, ease: 'power2.in' })
      .add(() => setDisplayedCap(captionFor(currentIdx)))
      .to(el, { opacity: 1, duration: 0.3, ease: 'power2.out' })
  }, [currentIdx, reduced])

  // ── Autoplay ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (autoPaused || reduced) return
    const id = setInterval(() => {
      setCurrentIdx(i => (i + 1) % N)
    }, 4000)
    return () => clearInterval(id)
  }, [autoPaused, reduced])

  // ── Entrance animation ───────────────────────────────────────────────────
  useEffect(() => {
    gsapSetup()
    const section = sectionRef.current
    if (!section) return

    if (reduced) {
      // Static reveal — final transforms, no motion
      applyTransforms(0, true)
      gsap.set([introRef.current, viewportRef.current, captionRef.current, dotsRef.current].filter(Boolean), { autoAlpha: 1, y: 0 })
      return
    }

    // Initial hidden states
    gsap.set(introRef.current, { autoAlpha: 0, y: 30 })
    gsap.set(viewportRef.current, { autoAlpha: 0 })
    gsap.set(captionRef.current, { autoAlpha: 0, y: 16 })
    gsap.set(dotsRef.current, { autoAlpha: 0, y: 12 })
    photoRefs.current.forEach((el, i) => {
      if (!el) return
      const n = dist(i, 0, N)
      const t = transformFor(n, cfgRef.current)
      gsap.set(el, {
        x: t.x, rotationY: t.rotationY,
        z: t.z - 260,          // enter from deeper
        scale: t.scale * 0.78,
        autoAlpha: 0,
        zIndex: t.zIndex,
      })
    })

    const tl = gsap.timeline({
      paused: true,
      defaults: { ease: 'power3.out' },
    })
    // Intro reveals first
    tl.to(introRef.current, { autoAlpha: 1, y: 0, duration: 0.7 }, 0)
      .to(viewportRef.current, { autoAlpha: 1, duration: 0.5 }, 0.3)

    // Photos fly into their curved positions — inside-out stagger
    photoRefs.current.forEach((el, i) => {
      if (!el) return
      const n = dist(i, 0, N)
      const t = transformFor(n, cfgRef.current)
      tl.to(el, {
        z: t.z, scale: t.scale, autoAlpha: t.opacity,
        duration: 0.8, ease: 'power3.out',
      }, 0.4 + Math.abs(n) * 0.08)
    })

    tl.to(captionRef.current, { autoAlpha: 1, y: 0, duration: 0.5 }, 1.2)
      .to(dotsRef.current,    { autoAlpha: 1, y: 0, duration: 0.5 }, 1.3)

    const trigger = ScrollTrigger.create({
      trigger: section,
      start: 'top 60%',
      once: true,
      onEnter: () => {
        tl.play()
        // Start auto-play 1.5s after entrance kicks off (spec)
        setTimeout(() => setAutoPaused(false), 1500)
      },
    })

    // Autoplay off until entrance fires
    setAutoPaused(true)

    return () => { trigger.kill(); tl.kill() }
  }, [reduced, applyTransforms])

  // ── Draggable — type:"x" on a proxy, with inertia + snap ────────────────
  useEffect(() => {
    if (reduced) return
    const proxy = dragProxyRef.current
    const trigger = viewportRef.current
    if (!proxy || !trigger) return

    const step = () => cfgRef.current.cardW - cfgRef.current.overlap

    const instance = Draggable.create(proxy, {
      type: 'x',
      trigger,
      inertia: true,
      dragResistance: 0.15,
      throwResistance: 2000,
      allowNativeTouchScrolling: false,
      cursor: 'grab',
      activeCursor: 'grabbing',
      minimumMovement: 6,
      onDragStart() {
        pauseAuto()
      },
      onDrag() {
        applyTransforms(currentIdxRef.current - this.x / step(), true)
      },
      onThrowUpdate() {
        applyTransforms(currentIdxRef.current - this.x / step(), true)
      },
      onThrowComplete() {
        const delta = Math.round(-this.x / step())
        const newIdx = ((currentIdxRef.current + delta) % N + N) % N
        gsap.set(proxy, { x: 0 })
        setCurrentIdx(newIdx)
        scheduleResumeAuto(3000)
      },
      snap: {
        x: (value: number) => Math.round(value / step()) * step(),
      },
    })

    return () => { instance.forEach(d => d.kill()) }
  }, [applyTransforms, pauseAuto, scheduleResumeAuto, reduced])

  // ── Navigation handlers ─────────────────────────────────────────────────
  const goTo = useCallback((idx: number) => {
    setCurrentIdx(((idx % N) + N) % N)
    pauseAuto()
    scheduleResumeAuto(3000)
  }, [pauseAuto, scheduleResumeAuto])

  const next = useCallback(() => goTo(currentIdxRef.current + 1), [goTo])
  const prev = useCallback(() => goTo(currentIdxRef.current - 1), [goTo])

  // Keyboard nav when viewport focused
  const onViewportKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight')      { e.preventDefault(); next() }
    else if (e.key === 'ArrowLeft')  { e.preventDefault(); prev() }
  }, [next, prev])

  // Photo click — side photos center themselves, center photo opens lightbox.
  // Draggable's minimumMovement threshold keeps drags from firing this.
  const onPhotoClick = useCallback((i: number) => {
    if (i !== currentIdxRef.current) goTo(i)
    else setLightboxOpen(true)
  }, [goTo])

  // Pause autoplay while lightbox is open; resume after close.
  useEffect(() => {
    if (lightboxOpen) pauseAuto()
    else scheduleResumeAuto(1500)
  }, [lightboxOpen, pauseAuto, scheduleResumeAuto])

  const closeLightbox = useCallback(() => setLightboxOpen(false), [])
  const lightboxPrev  = useCallback(() => setCurrentIdx(i => ((i - 1) % N + N) % N), [])
  const lightboxNext  = useCallback(() => setCurrentIdx(i => (i + 1) % N), [])
  const lightboxJump  = useCallback((i: number) => setCurrentIdx(((i % N) + N) % N), [])

  // Hover handlers for auto-play + arrow visibility
  const onViewportEnter = useCallback(() => {
    pauseAuto()
    setHoverChrome(true)
  }, [pauseAuto])
  const onViewportLeave = useCallback(() => {
    scheduleResumeAuto(1500)
    setHoverChrome(false)
  }, [scheduleResumeAuto])

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <section
      ref={sectionRef}
      id="interlude"
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '110vh',
        background: CREAM,
        color: EARTH_INK,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        isolation: 'isolate',
      }}
    >
      <style>{`
        .il-photo {
          position: absolute;
          left: 50%;
          top: 50%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(81,50,5,0.15), 0 6px 14px rgba(81,50,5,0.08);
          cursor: pointer;
          transform-style: preserve-3d;
          will-change: transform, opacity;
        }
        .il-photo img {
          filter: ${IMG_FILTER};
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
        }
        .il-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 48px; height: 48px;
          border-radius: 50%;
          border: 1px solid rgba(91,112,5,0.35);
          background: rgba(243,234,200,0.7);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center;
          color: ${OLIVE};
          cursor: pointer;
          opacity: 0;
          transition: opacity 300ms ease, transform 220ms ease, border-color 220ms ease, color 220ms ease;
          z-index: 20;
        }
        .il-arrow.visible { opacity: 1; }
        .il-arrow:hover {
          transform: translateY(-50%) scale(1.08);
          border-color: ${LIME};
          color: ${LIME};
        }
        .il-arrow:focus-visible {
          outline: 2px solid ${LIME}; outline-offset: 3px;
        }
        .il-dot {
          background: rgba(91,112,5,0.3);
          border: none;
          padding: 0;
          cursor: pointer;
          transition: background 220ms ease, transform 220ms ease;
        }
        .il-dot.active { background: ${LIME}; box-shadow: 0 0 6px ${LIME}; }
        .il-dot:focus-visible {
          outline: 2px solid ${LIME}; outline-offset: 3px;
        }
        /* Subtle vignette at section edges */
        .il-vignette::before, .il-vignette::after {
          content: ''; position: absolute; top: 0; bottom: 0; width: clamp(60px, 8vw, 140px);
          pointer-events: none; z-index: 5;
        }
        .il-vignette::before { left: 0;  background: linear-gradient(to right, ${CREAM} 0%, rgba(243,234,200,0) 100%); }
        .il-vignette::after  { right: 0; background: linear-gradient(to left,  ${CREAM} 0%, rgba(243,234,200,0) 100%); }

        @media (max-width: 767px) {
          .il-arrow { width: 36px; height: 36px; opacity: 1; }
          .il-arrow.visible { opacity: 1; }
          .il-vignette::before,
          .il-vignette::after { width: 32px; }
        }

        /* ── Lightbox ─────────────────────────────────────────────── */
        .ilb-close {
          width: 40px; height: 40px;
          border-radius: 50%;
          border: 1px solid ${CREAM_30};
          background: transparent;
          color: ${CREAM_60};
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 220ms ease, color 220ms ease, transform 220ms ease, background 220ms ease;
        }
        .ilb-close:hover {
          border-color: ${LIME};
          color: ${LIME};
          transform: rotate(90deg);
          background: rgba(159,175,8,0.08);
        }
        .ilb-close:focus-visible {
          outline: 2px solid ${LIME};
          outline-offset: 3px;
        }
        .ilb-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 56px; height: 56px;
          border-radius: 50%;
          border: 1px solid ${CREAM_30};
          background: rgba(20,14,4,0.4);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          color: ${CREAM_60};
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: border-color 220ms ease, color 220ms ease, transform 220ms ease, background 220ms ease;
          z-index: 10;
        }
        .ilb-arrow:hover {
          border-color: ${LIME};
          color: ${LIME};
          background: rgba(159,175,8,0.08);
          transform: translateY(-50%) scale(1.08);
        }
        .ilb-arrow:focus-visible {
          outline: 2px solid ${LIME};
          outline-offset: 3px;
        }
        .ilb-arrow-left  { left:  clamp(16px, 2.5vw, 36px); }
        .ilb-arrow-right { right: clamp(16px, 2.5vw, 36px); }
        .ilb-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: ${CREAM_30};
          border: none; padding: 0;
          cursor: pointer;
          transition: background 220ms ease, transform 220ms ease, box-shadow 220ms ease;
        }
        .ilb-dot.active {
          background: ${LIME};
          box-shadow: 0 0 6px ${LIME};
          transform: scale(1.25);
        }
        .ilb-dot:hover { background: ${CREAM_60}; }
        .ilb-dot:focus-visible {
          outline: 2px solid ${LIME};
          outline-offset: 3px;
        }

        @media (max-width: 767px) {
          .ilb-arrow { width: 42px; height: 42px; }
          /* Section-level: chrome labels + intro headline tighten */
          #interlude > div[aria-hidden="true"][style*="top: 28px"] {
            top: 14px !important;
            font-size: 9px !important;
            letter-spacing: 0.28em !important;
          }
          #interlude > div[aria-hidden="true"][style*="left: clamp(24px"] {
            left: 14px !important;
          }
          #interlude > div[aria-hidden="true"][style*="right: clamp(24px"] {
            right: 14px !important;
          }
          #interlude h2 {
            font-size: clamp(36px, 9.6vw, 56px) !important;
          }
          #interlude p[style*="0.38em"] {
            font-size: 9px !important;
            letter-spacing: 0.32em !important;
          }
        }
      `}</style>

      {/* ── Editorial chrome ─────────────────────────────────────────── */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 28, left: 'clamp(24px, 4vw, 56px)', zIndex: 15,
        fontFamily: FONT_BODY, fontSize: 10, letterSpacing: '0.36em',
        textTransform: 'uppercase', fontWeight: 600, color: INK_LOW,
      }}>{t('chromeLeft')}</div>
      <div aria-hidden="true" style={{
        position: 'absolute', top: 28, right: 'clamp(24px, 4vw, 56px)', zIndex: 15,
        fontFamily: FONT_BODY, fontSize: 10, letterSpacing: '0.36em',
        textTransform: 'uppercase', fontWeight: 600, color: INK_LOW,
      }}>{t('chromeRight')}</div>

      {/* ── Grain overlay ────────────────────────────────────────────── */}
      <div aria-hidden="true" style={{
        position: 'absolute', inset: 0,
        backgroundImage: 'url(/grain.svg)',
        backgroundSize: '200px 200px',
        opacity: 0.06, mixBlendMode: 'multiply',
        pointerEvents: 'none', zIndex: 4,
      }} />

      {/* ── TOP INTRO ─────────────────────────────────────────────────── */}
      {/* Outer band spans the section's real width; inner uses flex
          justify-content to center the content column regardless of any
          100vw sibling that might widen the flex-column's content box. */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: 'clamp(80px, 12vh, 140px) clamp(24px, 4vw, 56px) clamp(24px, 3vw, 40px)',
          position: 'relative',
          zIndex: 6,
        }}
      >
        <div
          ref={introRef}
          style={{
            width: '100%',
            maxWidth: 820,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <p style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            letterSpacing: '0.38em',
            textTransform: 'uppercase',
            fontWeight: 600,
            color: OLIVE,
            margin: '0 0 clamp(18px, 2vh, 26px)',
            lineHeight: 1,
          }}>
            {t('eyebrowPre')} · {t('eyebrowMid')} · {t('eyebrowEnd')}
          </p>
          <h2 style={{
            fontFamily: FONT_DISP,
            fontWeight: 500,
            fontSize: 'clamp(2.75rem, 5.4vw, 4.5rem)',
            lineHeight: 1.0,
            letterSpacing: '-0.025em',
            color: EARTH_INK,
            margin: '0 0 clamp(18px, 2.2vh, 28px)',
          }}>
            {t('headlinePre')}{' '}
            <em style={{
              fontFamily: FONT_SERIF,
              fontStyle: 'italic',
              fontWeight: 400,
              color: OLIVE,
              letterSpacing: '-0.01em',
            }}>
              {t('headlineItalic')}
            </em>
          </h2>
          <p style={{
            fontFamily: FONT_BODY,
            fontWeight: 400,
            fontSize: 'clamp(15px, 1.15vw, 18px)',
            lineHeight: 1.6,
            color: EARTH_MID,
            margin: 0,
            maxWidth: '52ch',
          }}>
            {t('subhead')}
          </p>
        </div>
      </div>

      {/* ── SLIDER VIEWPORT ───────────────────────────────────────────── */}
      <div
        ref={viewportRef}
        role="region"
        aria-roledescription="carousel"
        aria-label={t('galleryLabel')}
        tabIndex={0}
        onMouseEnter={onViewportEnter}
        onMouseLeave={onViewportLeave}
        onFocus={onViewportEnter}
        onBlur={onViewportLeave}
        onKeyDown={onViewportKey}
        className="il-vignette"
        style={{
          position: 'relative',
          width: '100%',
          height: isMobile ? '50vh' : '60vh',
          minHeight: isMobile ? 340 : 480,
          overflow: 'hidden',
          perspective: '1500px',
          outline: 'none',
          touchAction: 'pan-y',
        }}
      >
        {/* Stage (preserves 3D for descendants) */}
        <div
          ref={stageRef}
          style={{
            position: 'absolute', inset: 0,
            transformStyle: 'preserve-3d',
            pointerEvents: 'none',
          }}
        >
          {PHOTOS.map((p, i) => {
            const photoAlt = p.alt ?? tp(`${p.key}Alt`)
            return (
              <div
                key={i}
                ref={el => { photoRefs.current[i] = el }}
                className="il-photo"
                onClick={() => onPhotoClick(i)}
                role="button"
                tabIndex={-1}
                aria-label={`${i + 1}/${N} · ${photoAlt}`}
                style={{
                  width: cfg.cardW,
                  height: cfg.cardH,
                  marginLeft: -cfg.cardW / 2,
                  marginTop:  -cfg.cardH / 2,
                  pointerEvents: 'auto',
                }}
              >
                <Image
                  src={p.src}
                  alt={photoAlt}
                  fill
                  sizes={isMobile ? '240px' : '360px'}
                  style={{ objectFit: 'cover' }}
                  priority={i === 0}
                />
              </div>
            )
          })}
        </div>

        {/* Drag proxy — invisible, Draggable moves this; viewport is the trigger */}
        <div
          ref={dragProxyRef}
          aria-hidden="true"
          style={{ position: 'absolute', width: 1, height: 1, top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}
        />

        {/* Arrow buttons */}
        <button
          type="button"
          className={`il-arrow ${hoverChrome || isMobile ? 'visible' : ''}`}
          style={{ left: 'clamp(16px, 3vw, 40px)' }}
          onClick={prev}
          aria-label={t('arrowPrev')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          type="button"
          className={`il-arrow ${hoverChrome || isMobile ? 'visible' : ''}`}
          style={{ right: 'clamp(16px, 3vw, 40px)' }}
          onClick={next}
          aria-label={t('arrowNext')}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <polyline points="9 6 15 12 9 18" />
          </svg>
        </button>
      </div>

      {/* ── Caption ──────────────────────────────────────────────────── */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          textAlign: 'center',
          padding: 'clamp(24px, 3vw, 40px) clamp(24px, 4vw, 56px) clamp(12px, 2vw, 24px)',
          position: 'relative',
          zIndex: 6,
        }}
      >
        <p
          ref={captionRef}
          style={{
            fontFamily: FONT_SERIF, fontStyle: 'italic',
            fontSize: 'clamp(1rem, 1.4vw, 1.25rem)',
            color: EARTH_INK, margin: 0, minHeight: '1.5em',
          }}
        >
          {displayedCap}
        </p>
      </div>

      {/* ── Dots ─────────────────────────────────────────────────────── */}
      <div
        ref={dotsRef}
        role="tablist"
        aria-label={t('galleryLabel')}
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 8,
          padding: '0 clamp(24px, 4vw, 56px) clamp(56px, 8vh, 80px)',
          position: 'relative',
          zIndex: 6,
        }}
      >
        {PHOTOS.map((_, i) => {
          const active = i === currentIdx
          return (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={t('dotLabel', { n: i + 1 })}
              className={`il-dot ${active ? 'active' : ''}`}
              onClick={() => goTo(i)}
              style={{
                width:  active ? 10 : 8,
                height: active ? 10 : 8,
                borderRadius: '50%',
              }}
            />
          )
        })}
      </div>

      {/* ── Lightbox (branded fullscreen zoom) ────────────────────── */}
      <Lightbox
        photos={PHOTOS}
        open={lightboxOpen}
        photoIdx={currentIdx}
        onClose={closeLightbox}
        onPrev={lightboxPrev}
        onNext={lightboxNext}
        onJump={lightboxJump}
      />
    </section>
  )
}
